import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "../shared/layout";
import { InvoiceCard, SearchView } from "../../../components";
import { getInvoices, downloadInvoicesExcel } from "../../../data";
import ListView from "../../../components/list-view";
import SelectableListView from "../../../components/selectable-list-view";
import { useTableView } from "@/hooks/useTableView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  approveInvoice,
  downloadFilteredInvoicesAllExcel,
  downloadFilteredInvoicesExcel,
  getAllInvoices,
  getAllInvoicesOperations,
  uploadInvoiceStatusExcel,
  uploadBulkPaymentExcel,
  downloadFilteredInvoicesAllBankExcel,
  getPaymentHistoryGroups,
  getPaymentHistoryByGroupId,
  downloadGroupInvoicePdfs,
  getCommissionCountryGroupedInvoiceIds,
} from "@/data/requests/invoice-requests";
import { getUsers } from "@/data/requests/user-requests";
import { filterRelatedUsers } from "@/utils/related-user-options";
import { countryOptions } from "@/utils/country-options";
import { useToast } from "@/components/ui/use-toast";
import * as XLSX from "xlsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import { Checkbox } from "@/components/ui/checkbox";

const STATUS_OPTIONS = [
  { value: "Approved", label: "Onaylandı" },
  { value: "Change Requested", label: "Değişiklik İstendi" },
  { value: "Troubled", label: "Sorunlu" },
  { value: "Invoice Uploaded", label: "Fatura Yüklendi" },
  { value: "Mail Sended", label: "Mail Gönderildi" },
  { value: "Accounting", label: "Muhasebe" },
  { value: "Auto Created Invoice", label: "Otomatik Oluşturulan Fatura" },
  { value: "OCR Completed", label: "OCR Tamamlandı" },
  { value: "LUCA", label: "LUCA" },
  { value: "WAITING", label: "BEKLİYOR" },
  { value: "InBank", label: "Bankada" },
  { value: "Paid", label: "Ödendi" },
  { value: "PAID-TIMEOUT", label: "ÖDEME-ZAMAN AŞIMI" },
  { value: "Not Paid", label: "Ödenmedi" },
  { value: "Return", label: "İade" },
  { value: "all", label: "Tümü" },
];

const DEFAULT_SEARCH_PARAMS = {
  name: "",
  email: "",
  lucaId: "",
  managerName: "",
  status: "",
  country: "",
  relatedUser: "",
};

export function InvoiceListOperationScreen() {
  const { isAdvancedTable } = useTableView();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  const [pageSize] = useState(10);
  const [searchParams, setSearchParams] = useState(DEFAULT_SEARCH_PARAMS);

  const [isInvoiceSelectionOpen, setIsInvoiceSelectionOpen] = useState(false);
  const [groupedInvoices, setGroupedInvoices] = useState([]);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);

  // Bulk Excel Results Dialog State
  const [isBulkResultsOpen, setIsBulkResultsOpen] = useState(false);
  const [bulkResults, setBulkResults] = useState({ success: [], failed: [] });

  const navigate = useNavigate();

  // Kullanıcı listesini çek
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ["users"],
    queryFn: getUsers,
  });

  const [page, setPage] = useState(() => {
    const storedPage = localStorage.getItem(`invoiceListPage-operations`);
    return storedPage ? parseInt(storedPage, 10) : 1;
  });

  const [queryParams, setQueryParams] = useState(() => {
    const storedQueryParams = localStorage.getItem(
      `invoiceListQueryParams-operations`
    );
    if (storedQueryParams && storedQueryParams !== "{}") {
      const parsed = JSON.parse(storedQueryParams);
      const merged = { ...DEFAULT_SEARCH_PARAMS, ...parsed };
      setSearchParams(merged);
      return merged;
    }
    return DEFAULT_SEARCH_PARAMS;
  });
  useEffect(() => {
    localStorage.setItem(`invoiceListPage-operations`, page.toString());
    localStorage.setItem(
      `invoiceListQueryParams-operations`,
      JSON.stringify(queryParams)
    );
  }, [page, queryParams]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["invoices", page, pageSize, queryParams, sortColumn, sortDirection],
    queryFn: () =>
      getAllInvoicesOperations({
        page,
        pageSize,
        nameFilter: queryParams.name,
        emailNameFilter: queryParams.email,
        lucaIdFilter: queryParams.lucaId,
        statusFilter: queryParams.status,
        countryFilter: queryParams.country,
        managerNameFilter: queryParams.managerName,
        relatedUserFilter: queryParams.relatedUser,
        sortColumn,
        sortDirection,
      }),
    onError: (error) => {
      toast({
        title: "Faturalar yüklenirken hata oluştu",
        description:
          error.response?.data?.message || "Beklenmeyen bir hata oluştu",
        variant: "destructive",
      });
    },
  });

  const downloadExcel = () => {
    if (queryParams.status !== "LUCA" && queryParams.status !== "InBank") {
      toast({
        title: "Excel dosyası indirilirken hata oluştu",
        description: "Durum filtresi LUCA veya Bankada olmalı",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingInvoices(true);

    // Get invoice IDs and open the selection dialog
    getCommissionCountryGroupedInvoiceIds(queryParams.country)
      .then((response) => {
        console.log("Invoices response:", response.data);

        // Ensure data has the expected structure
        if (response.data && Array.isArray(response.data)) {
          setGroupedInvoices(response.data);
        } else {
          console.error("Unexpected API response format:", response.data);
          setGroupedInvoices([]);
          toast({
            title: "API Yanıt Formatı Hatası",
            description: "Sunucu beklenmeyen bir veri formatı döndürdü",
            variant: "destructive",
          });
        }

        setSelectedInvoiceIds([]);
        setIsInvoiceSelectionOpen(true);
        setIsLoadingInvoices(false);
      })
      .catch((error) => {
        console.error("Error fetching invoices:", error);
        setIsLoadingInvoices(false);
        toast({
          title: "Fatura numaraları yüklenirken hata oluştu",
          description:
            error.response?.data?.message || "Beklenmeyen bir hata oluştu",
          variant: "destructive",
        });
      });
  };

  const handleDownloadSelectedInvoices = () => {
    if (selectedInvoiceIds.length === 0) {
      toast({
        title: "Fatura seçilmedi",
        description: "İndirmek için en az bir fatura seçin",
        variant: "destructive",
      });
      return;
    }

    // Join the selected invoice IDs with commas
    const invoiceIdsString = selectedInvoiceIds.join(",");

    // Show a loading toast
    toast({
      title: "İndirme hazırlanıyor",
      description: `Excel dosyası oluşturuluyor: ${selectedInvoiceIds.length} invoices...`,
      variant: "default",
    });

    downloadFilteredInvoicesAllBankExcel({
      nameFilter: queryParams.name,
      emailNameFilter: queryParams.email,
      lucaIdFilter: queryParams.lucaId,
      statusFilter: queryParams.status,
      countryFilter: queryParams.country,
      invoiceIdFilter: invoiceIdsString ? invoiceIdsString : undefined,
      managerNameFilter: queryParams.managerName,
    })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "bank_account_invoices.xlsx");
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Close the dialog and show success message
        setIsInvoiceSelectionOpen(false);
        toast({
          title: "İndirme başarılı",
          description: `${selectedInvoiceIds.length} fatura içeren Excel dosyası indirildi.`,
          variant: "default",
        });
      })
      .catch((error) => {
        console.error("Error downloading Excel:", error);
        toast({
          title: "Excel dosyası indirilirken hata oluştu",
          description:
            error.response?.data?.message || "Beklenmeyen bir hata oluştu",
          variant: "destructive",
        });
      });
  };

  const selectAllInvoices = () => {
    if (groupedInvoices && groupedInvoices.length > 0) {
      // Extract all invoice IDs
      const allIds = groupedInvoices.map((item) => item.invoiceId);
      setSelectedInvoiceIds(allIds);
    }
  };

  const toggleInvoiceSelection = (invoiceId) => {
    setSelectedInvoiceIds((prev) => {
      if (prev.includes(invoiceId)) {
        return prev.filter((id) => id !== invoiceId);
      } else {
        return [...prev, invoiceId];
      }
    });
  };

  const clearAllInvoices = () => {
    setSelectedInvoiceIds([]);
  };

  const uploadBankExcel = (event) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("file", file);

      // Show loading notification
      toast({
        title: "Excel dosyası yükleniyor...",
        description: "Dosyanız işlenirken lütfen bekleyin.",
        variant: "default",
      });

      // Validate the Excel file before uploading
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          // Check if the Excel file has data
          if (!jsonData || jsonData.length === 0) {
            toast({
              title: "Excel dosyasında hata",
              description: "Excel dosyası boş veya veri içermiyor.",
              variant: "destructive",
            });
            document.getElementById("uploadExcel").value = "";
            return;
          }

          // Check if required fields exist in the first row
          const firstRow = jsonData[0];
          if (
            !firstRow.hasOwnProperty("id") ||
            !firstRow.hasOwnProperty("amount")
          ) {
            toast({
              title: "Excel dosyasında hata",
              description:
                "Excel dosyasında zorunlu kolonlar eksik: 'id' ve 'amount'. Lütfen örnek şablonu indirin.",
              variant: "destructive",
            });
            document.getElementById("uploadExcel").value = "";
            return;
          }

          // If validation passes, proceed with upload
          uploadBulkPaymentExcel(formData)
            .then((response) => {
              // Set results and open dialog
              setBulkResults(response.data.results);
              setIsBulkResultsOpen(true);

              // Refresh the data
              queryClient.invalidateQueries([
                "invoices",
                page,
                pageSize,
                queryParams,
              ]);

              // Also refresh payment history groups
              queryClient.invalidateQueries(["paymentHistoryGroups"]);
            })
            .catch((error) => {
              // Show error message with more details
              let errorMessage = "Beklenmeyen bir hata oluştu";

              if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
              }

              // Add more specific error details if available
              if (error.response?.data?.error) {
                errorMessage += `: ${error.response.data.error}`;
              }

              // Handle specific errors
              if (
                error.response?.data?.error?.includes(
                  "Cannot read properties of undefined"
                )
              ) {
                errorMessage =
                  "Excel format hatası: Excel dosyasının yapısı beklenenle uyuşmuyor. Dosyanızda zorunlu alanların (id ve amount) bulunduğundan ve id değerlerinin mevcut faturalarla eşleştiğinden emin olun. Referans için örnek şablonu indirin.";
              }

              toast({
                title: "Excel dosyası yüklenirken hata oluştu",
                description: errorMessage,
                variant: "destructive",
              });

              console.error("Error uploading Excel:", error);

              // Reset the file input
              document.getElementById("uploadExcel").value = "";
            });
        } catch (error) {
          toast({
            title: "Excel dosyası okunurken hata oluştu",
            description:
              "Dosya Excel dosyası olarak okunamadı. Lütfen dosya formatını kontrol edin.",
            variant: "destructive",
          });
          document.getElementById("uploadExcel").value = "";
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleUploadClick = () => {
    document.getElementById("uploadExcel").click();
  };

  const handleSearch = (item) => {
    const newSearchParams = { ...searchParams, [item.key]: item.value };
    setSearchParams(newSearchParams);

    setTimeout(() => {
      setQueryParams(newSearchParams);
      setPage(1);
    }, 300);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return format(date, "dd.MM.yyyy HH:mm");
    } catch (error) {
      return dateString;
    }
  };

  const pageSettings = {
    title: "Fatura Operasyonları",
    actions: [
      {
        name: "Banka Excel'i İndir",
        color: "bg-blue-500 hover:bg-blue-700 text-white",
        onClick: downloadExcel,
      },
      {
        name: "Toplu Ödeme Excel'i Yükle",
        color: "bg-green-500 hover:bg-green-700 text-white",
        onClick: handleUploadClick,
      },
      {
        name: "Ödeme (Ödenen) Geçmişi",
        color: "bg-purple-500 hover:bg-purple-700 text-white",
        onClick: () => {
          navigate("/payment-history/list");
        },
      },
    ],
  };

  // Eski tablo için basit kolonlar
  const simpleColumns = [
    {
      key: "name",
      label: "Ad",
      accessor: (item) => item.commission?.client?.name || "",
    },
    {
      key: "email",
      label: "E-posta",
      accessor: (item) => item.commission?.email || "",
    },
    {
      key: "ourInvoiceId",
      label: "Fatura Tarihimiz",
      accessor: (item) => item.commission?.invoiceId || "",
    },
    {
      key: "invoiceId",
      label: "Fatura No",
      accessor: (item) => item.invoiceId || "",
    },
    {
      key: "bankAccountHolder",
      label: "Hesap Sahibi",
      accessor: (item) => item.bankAccountHolder || "",
    },
    {
      key: "lucaAccountName",
      label: "Luca Hesap Adı",
      accessor: (item) => item.commission?.client?.lucaAccountName || "",
    },
    {
      key: "lucaId",
      label: "Luca ID",
      accessor: (item) => item.commission?.client?.lucaId || "",
    },
    {
      key: "finalAmount",
      label: "Net Tutar",
      accessor: (item) => item.commission?.finalAmount || "",
    },
    {
      key: "status",
      label: "Durum",
      render: (row) => (
        <div className="flex items-center gap-2">
          <span>{row.status}</span>
          {row.status === "Return" && row.returnReason && (
            <div className="relative group">
              <svg
                className="h-4 w-4 text-blue-500 cursor-help"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none">
                Return Reason: {row.returnReason}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>
          )}
          {row.status === "Paid" && row.paymentDate && (
            <div className="relative group">
              <svg
                className="h-4 w-4 text-blue-500 cursor-help"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none">
                Paid Date: {row.paymentDate}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      label: "İşlemler",
      render: (row) => (
        <div className="flex gap-2">
          <Button
            onClick={() =>
              navigate(
                `/payments/${row.commission.paymentId}/invoices/${row.id}`
              )
            }
          >
            Detayları Gör
          </Button>
        </div>
      ),
    },
  ];

  // Gelişmiş tablo için tüm kolonlar
  const advancedColumns = [
    { key: "id", label: "ID", sortable: true },
    {
      key: "clientName",
      label: "Müşteri Adı",
      sortable: false,
      accessor: (item) => item.commission?.client?.name || "",
    },
    {
      key: "influencerName",
      label: "Influencer",
      sortable: false,
      accessor: (item) => item.commission?.influencerName || "",
    },
    {
      key: "commissionEmail",
      label: "Komisyon E-postası",
      sortable: false,
      accessor: (item) => item.commission?.email || "",
    },
    { key: "email", label: "Fatura E-postası", sortable: true },
    { key: "phoneNumber", label: "Telefon", sortable: true },
    { key: "recipientName", label: "Alıcı Adı", sortable: true },
    { key: "bankAccountHolder", label: "Hesap Sahibi", sortable: true },
    { key: "iban", label: "IBAN", sortable: false },
    { key: "bic", label: "BIC", sortable: false },
    { key: "bankName", label: "Banka Adı", sortable: true },
    { key: "totalAmount", label: "Toplam Tutar", sortable: true },
    { key: "currency", label: "Para Birimi", sortable: true },
    { key: "invoiceId", label: "Fatura No", sortable: true },
    { key: "invoiceDate", label: "Fatura Tarihi", sortable: true },
    { key: "dueDate", label: "Vade Tarihi", sortable: true },
    { key: "ourCompany", label: "Şirketimiz", sortable: true },
    { key: "status", label: "Durum", sortable: true },
    { key: "paymentDate", label: "Ödeme Tarihi", sortable: true },
    { key: "returnDate", label: "İade Tarihi", sortable: true },
    { key: "returnReason", label: "İade Sebebi", sortable: false },
    {
      key: "commissionInvoiceId",
      label: "Fatura Tarihimiz",
      sortable: false,
      accessor: (item) => item.commission?.invoiceId || "",
    },
    {
      key: "finalAmount",
      label: "Net Tutar",
      sortable: false,
      accessor: (item) => item.commission?.finalAmount || "",
    },
    {
      key: "managerName",
      label: "Menajer",
      sortable: false,
      accessor: (item) => item.commission?.managerName || "",
    },
    {
      key: "country",
      label: "Ülke",
      sortable: false,
      accessor: (item) => item.commission?.country || "",
    },
    {
      key: "lucaId",
      label: "Luca ID",
      sortable: false,
      accessor: (item) => item.commission?.client?.lucaId || "",
    },
    {
      key: "lucaAccountName",
      label: "Luca Hesap Adı",
      sortable: false,
      accessor: (item) => item.commission?.client?.lucaAccountName || "",
    },
    {
      key: "clientCurrency",
      label: "Müşteri Para Birimi",
      sortable: false,
      accessor: (item) => item.commission?.client?.currency || "",
    },
    {
      key: "clientBankName",
      label: "Müşteri Bankası",
      sortable: false,
      accessor: (item) => item.commission?.client?.bankName || "",
    },
    {
      key: "clientIBAN",
      label: "Müşteri IBAN",
      sortable: false,
      accessor: (item) => item.commission?.client?.IBAN || "",
    },
    {
      key: "createdAt",
      label: "Oluşturulma",
      sortable: true,
      accessor: (item) => item.createdAt ? new Date(item.createdAt).toLocaleDateString("tr-TR") : "",
    },
    {
      key: "actions",
      label: "İşlemler",
      sortable: false,
      render: (row) => (
        <div className="flex gap-2">
          <Button size="sm" onClick={() => navigate(`/payments/${row.commission?.paymentId}/invoices/${row.id}`)}>
            Görüntüle
          </Button>
        </div>
      ),
    },
  ];

  const defaultVisibleColumns = ["clientName", "commissionEmail", "totalAmount", "status", "country", "lucaId", "actions"];

  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  return (
    <AppLayout
      fullscreen={true}
      title={pageSettings.title}
      actions={pageSettings.actions}
      loading={isLoading}
      style={{ padding: "0 10px" }}
    >
      <input
        type="file"
        id="uploadExcel"
        style={{ display: "none" }}
        onChange={uploadBankExcel}
        accept=".xlsx,.xls"
      />
      <SearchView
        searchParams={searchParams}
        handleSearch={handleSearch}
        statusOptions={STATUS_OPTIONS}
        countryOptions={countryOptions}
        resetDefault={() => {
          setSearchParams(DEFAULT_SEARCH_PARAMS);
          setQueryParams(DEFAULT_SEARCH_PARAMS);
          setPage(1);
        }}
        relatedUserOptions={filterRelatedUsers(usersData?.data).map((user) => ({
          value: user.name || user.email,
          label: `${user.name} ${user.email ? `(${user.email})` : ""}`,
        })) || []}
        relatedUserLoading={usersLoading}
      />
      {isAdvancedTable ? (
        <SelectableListView
          isLoading={isLoading}
          data={data?.data?.commissionDetails}
          columns={advancedColumns}
          columnVisibilityKey="invoices-operations-columns"
          defaultVisibleColumnKeys={defaultVisibleColumns}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSortChange={handleSort}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) => navigate(`/payments/${item.commission?.paymentId}/invoices/${item.id}`)}
        />
      ) : (
        <ListView
          isLoading={isLoading}
          data={data?.data?.commissionDetails}
          columns={simpleColumns}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) => navigate(`/payments/${item.commission?.paymentId}/invoices/${item.id}`)}
          renderGridItem={(item) => (
            <InvoiceCard
              key={item.id}
              name={item.commission?.client?.name}
              email={item.email}
              invoiceId={item.commission?.invoiceId}
              finalAmount={item.commission?.finalAmount}
              status={item.status}
              returnReason={item.returnReason}
              onDetailClick={() =>
                navigate(
                  `/payments/${item.commission.paymentId}/invoices/${item.id}`
                )
              }
            />
          )}
        />
      )}

      {/* Invoice Selection Dialog */}
      <Dialog
        open={isInvoiceSelectionOpen}
        onOpenChange={setIsInvoiceSelectionOpen}
      >
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fatura Seç</DialogTitle>
            <DialogDescription>
              Excel indirmesine dahil edilecek faturaları seçin
            </DialogDescription>
          </DialogHeader>

          {isLoadingInvoices ? (
            <div className="flex justify-center p-4">
              Fatura verileri yükleniyor...
            </div>
          ) : (
            <>
              <div className="flex justify-between mb-4">
                <Button variant="outline" onClick={selectAllInvoices}>
                  Tümünü Seç
                </Button>
                <Button variant="outline" onClick={clearAllInvoices}>
                  Temizle
                </Button>
              </div>

              {groupedInvoices && groupedInvoices.length > 0 ? (
                <div className="space-y-2">
                  {groupedInvoices.map((item) => (
                    <div
                      key={item.invoiceId}
                      className="flex items-center space-x-2 p-2 border rounded hover:bg-muted"
                    >
                      <Checkbox
                        id={`invoice-${item.invoiceId}`}
                        checked={selectedInvoiceIds.includes(item.invoiceId)}
                        onCheckedChange={() =>
                          toggleInvoiceSelection(item.invoiceId)
                        }
                      />
                      <Label
                        htmlFor={`invoice-${item.invoiceId}`}
                        className="flex-1 cursor-pointer"
                      >
                        {item.invoiceId}
                      </Label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-4">
                  Mevcut filtrelere uyan fatura bulunamadı
                </div>
              )}

              <div className="mt-4">
                <p className="text-sm font-medium mb-2">
                  Selected Invoice IDs:{" "}
                  <span className="text-blue-600 font-medium">
                    {selectedInvoiceIds.length}
                  </span>{" "}
                  selected
                </p>
                {selectedInvoiceIds.length > 0 && (
                  <div className="p-2 bg-muted rounded-md max-h-[100px] overflow-y-auto">
                    <p className="text-sm break-all font-mono">
                      {selectedInvoiceIds.join(",")}
                    </p>
                  </div>
                )}
              </div>

              <DialogFooter className="mt-4">
                <Button
                  variant="outline"
                  onClick={() => setIsInvoiceSelectionOpen(false)}
                >
                  İptal
                </Button>
                <Button onClick={handleDownloadSelectedInvoices}>
                  Excel İndir
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Excel Results Dialog */}
      <Dialog open={isBulkResultsOpen} onOpenChange={setIsBulkResultsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Toplu Ödeme Excel Sonuçları</DialogTitle>
            <DialogDescription>
              Successfully updated {bulkResults.success?.length || 0} invoices.
              {bulkResults.failed?.length > 0 && (
                <span className="text-red-500 ml-1">
                  Failed to update {bulkResults.failed.length} invoices.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Success Table */}
          {bulkResults.success?.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-green-600 mb-2">
                ✓ Successful ({bulkResults.success.length})
              </h3>
              <div className="max-h-[200px] overflow-y-auto border rounded">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Müşteri Adı</TableHead>
                      <TableHead>Eski Durum</TableHead>
                      <TableHead>Yeni Durum</TableHead>
                      <TableHead>Tutar</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bulkResults.success.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.id}</TableCell>
                        <TableCell>{item.clientName}</TableCell>
                        <TableCell>{item.oldStatus}</TableCell>
                        <TableCell className="text-green-600 font-medium">
                          {item.newStatus}
                        </TableCell>
                        <TableCell>{item.amount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Failed Table */}
          {bulkResults.failed?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-red-600 mb-2">
                ✗ Failed ({bulkResults.failed.length})
              </h3>
              <div className="max-h-[200px] overflow-y-auto border rounded">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Tutar</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>Hata</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bulkResults.failed.map((item, index) => (
                      <TableRow key={index} className="bg-red-50">
                        <TableCell>{item.row?.id || "-"}</TableCell>
                        <TableCell>{item.row?.amount || "-"}</TableCell>
                        <TableCell>{item.row?.status || "-"}</TableCell>
                        <TableCell className="text-red-600 text-sm">
                          {item.error}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button onClick={() => setIsBulkResultsOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
